import { useEffect, useState } from "react";
import { HelpCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import CriticalPinDialog from "../CriticalPinDialog";

export default function QuestionsTab() {
    const [questions, setQuestions] = useState(null);
    const [filter, setFilter] = useState("all");
    const [pinTarget, setPinTarget] = useState(null);
    const load = () => api.get("/questions").then((r) => setQuestions(r.data)).catch(() => setQuestions([]));
    useEffect(() => { load(); }, []);

    const deleteRecord = async (pin) => {
        try {
            await api.delete(`/admin/records/questions/${pinTarget.question_id}`, { headers: { "X-Critical-Pin": pin } });
            toast.success("Soru kaydı silindi");
            setPinTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        }
    };

    if (!questions) return <div className="h-48 animate-pulse rounded-2xl bg-muted" />;
    const filtered = questions.filter((q) =>
        filter === "all" ? true : filter === "overdue" ? q.overdue && !q.answer : filter === "pending" ? !q.answer : !!q.answer
    );

    return (
        <div data-testid="admin-questions-tab">
            <p className="mb-3 text-sm text-muted-foreground">Mağaza ↔ tedarikçi soru-cevapları — yanıt SLA'sı 1 takvim günü (Pazar hariç). Süresi geçenler kırmızı işaretlenir.</p>
            <div className="mb-4 flex flex-wrap items-center gap-2">
                {[["all", "Tümü"], ["pending", "Yanıt Bekleyen"], ["overdue", "Süresi Geçen"], ["answered", "Yanıtlanan"]].map(([v, l]) => (
                    <button key={v} onClick={() => setFilter(v)} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${filter === v ? "border-primary bg-primary text-primary-foreground" : "hover:border-terra-500/50"}`} data-testid={`questions-filter-${v}`}>{l}</button>
                ))}
                <span className="ml-auto text-xs text-muted-foreground">{filtered.length} kayıt</span>
            </div>
            <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                <div className="max-h-[520px] overflow-x-auto overflow-y-auto">
                    <table className="w-full text-sm" data-testid="admin-questions-table">
                        <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                            <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                                <th className="p-3">Taraflar</th><th className="p-3">Ürün</th><th className="p-3">Soru</th><th className="p-3">Yanıt</th><th className="p-3">SLA</th><th className="p-3">Durum</th><th className="p-3 text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((q) => (
                                <tr key={q.question_id} className={`border-b last:border-0 ${!q.answer && q.overdue ? "bg-red-50" : ""}`} data-testid={`question-row-${q.question_id}`}>
                                    <td className="p-3 text-xs text-muted-foreground">{q.buyer_company_name} → {q.supplier_name}</td>
                                    <td className="p-3 text-xs">{q.product_name || "—"}</td>
                                    <td className="max-w-52 p-3 text-xs"><p className="line-clamp-2">{q.content}</p><p className="text-muted-foreground">{fmtDate(q.created_at)}</p></td>
                                    <td className="max-w-52 p-3 text-xs">{q.answer ? <p className="line-clamp-2">{q.answer}</p> : "—"}</td>
                                    <td className="p-3 text-xs text-muted-foreground">{fmtDate(q.answer_due_at)}</td>
                                    <td className="p-3">
                                        {q.answer ? (
                                            <Badge variant="outline" className="border-emerald-200 bg-emerald-100 text-[10px] text-emerald-800">Yanıtlandı{q.answered_late ? " (geç)" : ""}</Badge>
                                        ) : q.overdue ? (
                                            <Badge variant="outline" className="border-red-300 bg-red-100 text-[10px] font-semibold text-red-700" data-testid={`question-overdue-badge-${q.question_id}`}>Süre Doldu</Badge>
                                        ) : (
                                            <Badge variant="outline" className="border-amber-200 bg-amber-100 text-[10px] text-amber-800">Bekliyor</Badge>
                                        )}
                                    </td>
                                    <td className="p-3 text-right">
                                        <Button size="sm" variant="outline" className="h-8 border-red-200 text-xs text-red-600 hover:bg-red-50" onClick={() => setPinTarget(q)} data-testid={`question-delete-${q.question_id}`}>
                                            <Trash2 className="h-3 w-3" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {!filtered.length && (
                                <tr><td colSpan={7} className="p-16 text-center text-sm text-muted-foreground" data-testid="questions-empty-admin">
                                    <HelpCircle className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />Bu filtrede soru kaydı yok.
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <CriticalPinDialog
                open={!!pinTarget}
                onClose={() => setPinTarget(null)}
                title="Soru Kaydını Sil"
                message={pinTarget ? `${pinTarget.buyer_company_name} → ${pinTarget.supplier_name} arasındaki soru kaydı kalıcı olarak silinecek. Bu işlem denetim kaydına alınır.` : ""}
                onSubmit={deleteRecord}
            />
        </div>
    );
}
